// eslint-disable-next-line import/no-unresolved
import { MediaPlayer, Debug } from 'dashjs';
import { merge, fromEvent, NEVER, of, interval } from 'rxjs';
import { map, filter, mapTo, share, switchMap, take, distinctUntilChanged, delay } from 'rxjs/operators';
import RendererHTML from './html';
import { DASH_TYPE, RENDERER_LIVE_STREAM_DETECTED } from '../types';
import {
  RENDERER_NETWORK_ERROR,
  RENDERER_MEDIA_ERROR,
  RENDERER_DEFAULT_ERROR,
  RENDERER_LIVE_STREAM_STALLED,
  RENDERER_PROTECTION_ERROR,
  AKAMAI_ERROR_HEADER
} from '../../../error/definitions';
import { safeTake, bufferConsecutiveEvents } from '../../../utils/rx-utils';
import { TV_PLATFORMS } from '../../media/types';
import { PLAYBACK_END } from '../../../store/types';

export default class RendererDash extends RendererHTML {
  constructor(media, playerConfig, videoUnlocker, isAd$) {
    super(media, playerConfig, videoUnlocker, isAd$);
    this.serverForcedQuality = media.video.quality !== null && media.video.quality !== undefined;
    this.playerConfig = playerConfig;

    const { preload, debug } = this.playerConfig;
    this.preload = preload;

    this.engine = MediaPlayer().create();
    this.engine$.next(this.engine);

    this.currentSettings = {
      debug: {
        logLevel: debug ? Debug.LOG_LEVEL_DEBUG : Debug.LOG_LEVEL_NONE
      },
      streaming: {
        abr: {
          autoSwitchBitrate: { video: !this.serverForcedQuality },
          initialBitrate: { audio: -1, video: media.video.quality }
        },
        text: {
          defaultEnabled: false
        },
        scheduling: {
          scheduleWhilePaused: preload
        },
        delay: {
          liveDelayFragmentCount: 4
        },
        buffer: {
          flushBufferAtTrackSwitch: TV_PLATFORMS.includes(playerConfig?.platform),
          fastSwitchEnabled: !TV_PLATFORMS.includes(playerConfig?.platform)
        }
      }
    };
    this.engine.updateSettings(this.currentSettings);
    this.manifestLoaded$ = fromEvent(this.engine, MediaPlayer.events.MANIFEST_LOADED).pipe(share());

    this.engine.initialize(this.tagElement);

    this.engine.setAutoPlay(false);
    this.engine.setInitialMediaSettingsFor('audio', { role: 'main' });

    RendererDash.createPlaybackEndStream(this.engine).safeSubscribe(this, this.videoOrAudioEventsOut$);

    const { drm } = media.video;
    if (drm) {
      RendererDash.setupDRM(this.engine, drm);
    }

    RendererDash.detectQualityChange({ engine: this.engine }).safeSubscribe(this, this.bitrate$);
  }

  static createPlaybackEndStream(engine) {
    return fromEvent(engine, MediaPlayer.events.PLAYBACK_ENDED).pipe(
      map(() => PLAYBACK_END)
    );
  }

  startLoad() {
    return !this.preload && this.engine.play();
  }

  async init(container, target$, media, interruptions$) {
    /* super RendererHTML::init sets this.tagElement */
    super.init(container, target$, media, interruptions$);

    await this.setupQuanteec(DASH_TYPE, media);

    this.tagElement.dirty = true;
    this.engine.attachView(this.tagElement);

    /* we need to create an empty DOM element for dash TTML handling to start working */
    this.engine.attachTTMLRenderingDiv(document.createElement('div'));
  }

  onMidrollStart() {
    this.pause();
    this.startPosition = Math.trunc(this.tagElement.currentTime);
    this.source = this.engine.getSource();
    this.engine.reset();
    setTimeout(() => this.suspendDuringMidroll$.next(true), 10);
  }

  onMidrollEnd() {
    this.suspendDuringMidroll$.next(false);
    this.engine = MediaPlayer().create();
    this.engine$.next(this.engine);

    this.engine.updateSettings(this.currentSettings);
    this.engine.initialize();
    // We don't reset the timestamp, this is done through rewind midroll, that goes back 10s before midroll started
    this.willAttach(this.source).then(() => {
      setTimeout(() => {
        this.engine.play();
      }, 100);
    });
  }

  configure(src) {
    this.engine.attachView(this.tagElement);
    this.engine.attachSource(src, this.startPosition);
  }

  dispose() {
    this.engine.reset();
    super.dispose();
  }

  createErrorStream() {
    const dashErrors$ = merge(
      /* Handle raw DashJS errors */
      this.engine$.pipe(switchMap(() => fromEvent(this.engine, MediaPlayer.events.ERROR)
        .pipe(map((data) => RendererDash.formatErrors(data))))),
      /* detect live stalled via successive seeking events */
      this.isLive$.pipe(switchMap((isLive) => (isLive
        ? this.videoOrAudioEventsOut$.pipe(
          filter((evt) => evt !== 'progress'),
          bufferConsecutiveEvents({
            event: 'seeking',
            count: 3
          }),
          mapTo({
            ...RENDERER_LIVE_STREAM_STALLED,
            code: RENDERER_LIVE_STREAM_STALLED.code + 200
          })
        ) : NEVER)))
    );

    const fatal$ = this.createRecoveryFailedStream(dashErrors$); /* implemented in GenericRenderer */

    const errors$ = merge(
      dashErrors$,
      fatal$,
      super.createErrorStream(this.isLive$) /* RendererHTML errors */
    );

    /* We do not want ad errors to trigger in the renderer.
     We delay a little bit in case the last ad is failing.
     In this case, the video tag would send the error after we think the midroll has ended
     and thus triggering an error on the renderer */
    return this.isMidroll$.pipe(
      delay(10),
      switchMap((isMidroll) => (isMidroll ? NEVER : errors$))
    );
  }

  createTimeshiftingDuration() {
    return merge(
      of(0),
      this.engine$.pipe(
        switchMap((engine) => fromEvent(engine, MediaPlayer.events.STREAM_INITIALIZED).pipe(
          switchMap((e) => {
            const { streamInfo: { manifestInfo } } = e;
            return interval(1000).pipe(map(() => {
              const now = Date.now();
              const startDate = manifestInfo.availableFrom.getTime();
              return (now - startDate) / 1000;
            }));
          }),
          distinctUntilChanged()
        ))
      )
    );
  }

  handleError({ error: { fatal, type, canRecover } }) {
    if (canRecover) {
      switch (type) {
        case RENDERER_NETWORK_ERROR.type: {
          this.engine.attachSource(this.engine.getSource());
          return this.engine.play();
        }
        case RENDERER_LIVE_STREAM_STALLED.type:
          return this.seek(this.engine.getDVRSeekOffset(this.engine.getDVRWindowSize()));
        default:
          break;
      }
    }
    return (fatal && this.dispose());
  }

  attachSource(source) {
    this.engine.attachView(this.tagElement);
    this.engine.attachSource(source, this.startPosition || undefined);
  }

  handleStartPosition() {
    fromEvent(this.engine, MediaPlayer.events.CAN_PLAY).pipe(take(1)).subscribe(() => this.seek(this.startPosition));
  }

  attachTracks() {
    fromEvent(this.engine, MediaPlayer.events.TEXT_TRACKS_ADDED)
      .pipe(map(({ tracks }) => tracks.map((track, index) => ({ kind: 'subtitles', ...track, index }))
        .filter((track) => track.mimeType === 'application/mp4')))
      .safeSubscribe(this, this.textTracks$);

    fromEvent(this.engine, MediaPlayer.events.CAN_PLAY)
      .pipe(take(1), map(this.getAudioTracks.bind(this)))
      .safeSubscribe(this, this.audioTracks$);
  }

  setVideoQuality(level) {
    if (this.serverForcedQuality) return;

    this.engine.updateSettings({
      streaming: {
        abr: {
          autoSwitchBitrate: { video: level < 0 }
        }
      }
    });
    if (level >= 0) {
      this.engine.setRepresentationForTypeByIndex('video', level);
    }
  }

  getAudioTracks() {
    const AUDIO_ORDER_DESC = [
      'description',
      'alternate',
      'main'
    ];

    const audioTracks = this.engine.getTracksFor('audio')
      .filter((track) => track.lang !== '')
      .sort((track1, track2) => {
        function getPrio(roles) {
          // no role is lower order than not found one
          if (!roles) {
            return -2;
          }

          return roles.reduce((prio, role) => AUDIO_ORDER_DESC.indexOf(role) + prio, 0);
        }

        return getPrio(track2.roles) - getPrio(track1.roles);
      })
      .map((track, index) => ({
        kind: 'audio', ...track, ...('index' in track ? {} : { index })
      }));
    return audioTracks;
  }

  createAvailableQualitiesStream() {
    return fromEvent(this.engine, MediaPlayer.events.STREAM_INITIALIZED)
      .pipe(map(() => this.engine.getRepresentationsByType('video')
        .sort((a, b) => a.qualityIndex - b.qualityIndex)));
  }

  play() {
    this.engine.play();
  }

  setAudioTrack(index) {
    const selectedTrack = this.getAudioTracks().find((track) => track.index === index);
    this.engine.setCurrentTrack(selectedTrack);
  }

  setTextTrack(index) {
    this.engine.setTextTrack(index);
  }

  detectLiveStream(media) {
    return this.manifestLoaded$.pipe(
      filter(({ data: { type } }) => type === 'dynamic' && !media.isLive),
      safeTake(1),
      mapTo(RENDERER_LIVE_STREAM_DETECTED)
    );
  }

  static detectQualityChange({ engine }) {
    return fromEvent(engine, MediaPlayer.events.QUALITY_CHANGE_RENDERED)
      .pipe(
        filter((data) => data?.mediaType === 'video'),
        map(({ newRepresentation: { index } }) => {
          const [{ bitrateList }] = engine.getTracksFor('video');
          const { bandwidth, height, width } = bitrateList[index];
          return {
            bandwidth,
            height,
            width,
            level: index
          };
        })
      );
  }

  static getHeader(networkDetails) {
    return networkDetails.getResponseHeader && networkDetails.getResponseHeader(AKAMAI_ERROR_HEADER);
  }

  static formatErrors(dashError) {
    const { error: { code, message, data } } = dashError;
    let error = {};
    switch (code) {
      /*
       * Single source of truth to escape the dash.js documentation maze:
       * https://cdn.dashjs.org/latest/jsdoc/core_errors_Errors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/mss_errors_MssErrors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/offline_errors_OfflineErrors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/streaming_protection_errors_ProtectionErrors.js.html
       */
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_MANIFEST_CODE: {
        error = RENDERER_NETWORK_ERROR;

        if (data.response?.status === 403) {
          error = RendererHTML.resolveAkamaiErrorFor(RendererDash, data.response);
        }
        break;
      }
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_XLINK_CODE:
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_INITIALIZATION_CODE:
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_CONTENT_CODE: {
        const isChunk = data?.request.type === 'MediaSegment';
        const isLowestQuality = data?.request.quality <= 1;
        error = {
          ...RENDERER_NETWORK_ERROR,
          fatal: !isChunk || (isChunk && isLowestQuality),
          canRecover: !(!isChunk || (isChunk && isLowestQuality))
        };
        break;
      }
      case MediaPlayer.errors.MEDIA_KEY_MESSAGE_ERROR_CODE:
      case MediaPlayer.errors.MEDIA_KEY_MESSAGE_LICENSER_ERROR_CODE: // fire after 4 status 500 on /licence
      case MediaPlayer.errors.TIMED_TEXT_ERROR_ID_PARSE_CODE: {
        error = RENDERER_PROTECTION_ERROR;
        break;
      }
      case MediaPlayer.errors.MANIFEST_ERROR_ID_NOSTREAMS_CODE:
      case MediaPlayer.errors.MANIFEST_ERROR_ID_MULTIPLEXED_CODE:
      case MediaPlayer.errors.MEDIASOURCE_TYPE_UNSUPPORTED_CODE: {
        error = RENDERER_MEDIA_ERROR;
        break;
      }
      default: {
        error = RENDERER_DEFAULT_ERROR;
        break;
      }
    }

    return ({
      fatal: true,
      ...error,
      code: error.code + 200,
      payload: { message, data },
      description: code
    });
  }

  static overriddenKeySystemWidevine() {
    /* We are currying the widevine method to pass the vudrm token  */
    return () => ({
      getLicenseRequestFromMessage(message) {
        const data = Uint8Array.from(btoa(message), (c) => c.charCodeAt(0));
        console.log(data);
        return message;
      }
    });
  }

  static setupDRM(engine, config) {
    // engine.extend(
    //   'KeySystemWidevine',
    //   RendererDash.overriddenKeySystemWidevine(),
    //   true
    // );

    engine.setProtectionData({
      'com.widevine.alpha': {
        serverURL: config.widevineLaUrl,
        httpRequestHeaders: {
          'nv-authorizations': config.token,
          'content-type': 'Application/octet-stream'
        }
      },
      'com.microsoft.playready': {
        serverURL: config.playReadyLaUrl,
        httpRequestHeaders: {
          'nv-authorizations': config.token
        }
      }
    });
  }
}
